import { ReactNode, useState } from 'react';

import { Add16 } from '@ingrid/iconography/react';
import { ButtonLink, Card, IconButton, ListLayout } from '@ingrid/luterkort-react';

import { AddressMultiline } from '~src/components/AddressMultiline/AddressMultiline';
import { ClearConfirmationModal } from '~src/components/ClearConfirmationModal/ClearConfirmationModal';
import { LocalizationKey, useTranslation } from '~src/i18n/use-translation';
import { useToast } from '~src/main/hooks/useToast';
import { FormValues } from '~src/pages/settings/address-book/components/address-book-form';
import { useAddressBookUpdateMutation } from '~src/pages/settings/address-book/mutations';

import { AddressType, useShipmentFormContext } from '../types';
import { AddressForm } from './address-form';
import { AddressSelect, AddressSelectProps } from './address-select';

interface Props {
  actions?: ReactNode;
  type: AddressType;
}

export function AddressCard({ actions, type }: Props) {
  const updateMutation = useAddressBookUpdateMutation();

  const { watch, setValue } = useShipmentFormContext();
  const { t } = useTranslation();
  const { toast } = useToast();

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const value = watch(type);

  const selectModalProps: Omit<AddressSelectProps, 'trigger'> = {
    onValueChange: contact => {
      setValue(type, contact, { shouldDirty: true });

      // Reset pickup point if consignee changes
      if (type === 'customer_info.contact') {
        setValue('location_type', undefined);
        setValue('location_ref', undefined);
      }
    },
    value,
    type,
  };

  async function handleEditSubmit(data: FormValues, saveToAddressBook: boolean) {
    if (saveToAddressBook) {
      const updatePromise = updateMutation.mutateAsync({
        ...data,
        additional_fields: { additional_fields: data.additional_fields },
        id: data?.id,
      });

      await toast.promise(updatePromise, {
        success: { title: t('addressBook.update.success') },
        error: { title: t('addressBook.update.error') },
        loading: { title: t('addressBook.update.loading') },
      });
    }

    // Reset pickup point if consignee changes
    if (type === 'customer_info.contact') {
      setValue('location_type', undefined);
      setValue('location_ref', undefined);
    }

    setValue(type, data, { shouldDirty: true });
    setIsEditModalOpen(false);
  }

  return (
    <Card.Wrapper style={{ height: '200px' }} data-testid={type}>
      {!value ? (
        <Card.Content>
          <ListLayout.Row>
            <Card.Header>{t(`shipments.address.type.${type}` as LocalizationKey)}</Card.Header>
            {actions && <ListLayout.Actions>{actions}</ListLayout.Actions>}
          </ListLayout.Row>

          <AddressSelect
            {...selectModalProps}
            trigger={
              <Card.Placeholder>
                <IconButton
                  aria-label={t('shipments.address.addAddress', {
                    type: t(`shipments.address.type.${type}` as LocalizationKey),
                  })}
                  data-testid="add-address"
                  variant="quaternary"
                  size="lg"
                >
                  <Add16 />
                </IconButton>
              </Card.Placeholder>
            }
          />
        </Card.Content>
      ) : (
        <Card.Content>
          <ListLayout.Column style={{ height: '100%', display: 'flex', justifyContent: 'space-between', gap: 0 }}>
            <ListLayout.Row>
              <Card.Header>{t(`shipments.address.type.${type}` as LocalizationKey)}</Card.Header>
              <ListLayout.Actions>
                <AddressSelect {...selectModalProps} trigger={<ButtonLink>{t('common.replace')}</ButtonLink>} />

                <ClearConfirmationModal
                  onSubmit={() => selectModalProps.onValueChange(undefined)}
                  clearedItemName={`shipments.address.type.${type}` as LocalizationKey}
                />

                <AddressForm
                  defaultValues={value}
                  isOpen={isEditModalOpen}
                  setIsOpen={setIsEditModalOpen}
                  trigger={<ButtonLink>{t('common.edit')}</ButtonLink>}
                  onSubmit={handleEditSubmit}
                  type={type === 'customer_info.contact' ? 'contact' : 'address'}
                  key={value.id}
                />
              </ListLayout.Actions>
            </ListLayout.Row>
            <ListLayout.Stack style={{ gap: 0 }}>
              <AddressMultiline
                email={type === 'customer_info.contact' ? value.email : undefined}
                phone={type === 'customer_info.contact' ? value.phone : undefined}
                address={value.address}
              />
            </ListLayout.Stack>
          </ListLayout.Column>
        </Card.Content>
      )}
    </Card.Wrapper>
  );
}
